import { Box, Grid, Stack } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { Common, Custom, Item } from "components";
import { RadarRounded } from "@mui/icons-material";
import { EnumTheme } from "utils/enums";

type Props = {};

export const Intro = (props: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  const headlineTokens = (t.intro.headline || "")
    .split("|")
    .map((token: string) => token.trim())
    .filter(Boolean);

  // Split intro.header into greeting prefix and name for editorial two-tier hero treatment
  const rawHeader = t.intro.header || "";
  const nameMatch = rawHeader.match(/(Sergio Righi\.?|Серхіо Рігі\.?|세르지오 리기.*)/i);
  const heroPrefix = nameMatch ? rawHeader.slice(0, nameMatch.index).trim() : "";
  const heroName = nameMatch ? nameMatch[0].trim() : rawHeader;

  return (
    <Box component="section" id="intro" sx={{ width: "100%", position: "relative" }}>
      <Box sx={{ width: "100%", mt: { xs: theme.spacing.lg, md: theme.spacing.xl } }}>
        <Grid
          container
          spacing={{ xs: theme.spacing.lg, md: theme.spacing.xl }}
          alignItems="center"
        >
          {/* Left Column: Cohesive, Minimalist AAA Editorial Hierarchy */}
          <Grid item xs={12} md={7} lg={7}>
            <Stack spacing={theme.spacing.md} sx={{ position: "relative" }}>
              {/* 1. Top Telemetry Ribbon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.spacing.sm,
                  flexWrap: "wrap",
                }}
              >
                {/* Pulsing Class Badge */}
                <Box
                  sx={{
                    px: theme.spacing.sm,
                    py: theme.spacing.xxs,
                    backgroundColor: theme.color.secondary.color,
                    color: theme.color.secondary.text,
                    fontFamily: '"Space Mono", monospace',
                    fontSize: theme.font.xxs,
                    fontWeight: theme.font.bold,
                    border: `1px solid ${theme.color.secondary.accent}`,
                    borderRadius: theme.border.square,
                    boxShadow: `2px 2px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      backgroundColor: theme.color.secondary.text,
                      display: "inline-block",
                      "@keyframes pulseBeacon": {
                        "0%, 100%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.3, transform: "scale(0.8)" },
                      },
                      animation: "pulseBeacon 2s infinite ease-in-out",
                    }}
                  />
                  {t.intro.badge_role}
                </Box>

                {/* Unframed Quest Tag */}
                <Box
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: theme.font.xxs,
                    fontWeight: theme.font.semiBold,
                    color: theme.color.font.accent,
                    letterSpacing: "0.08em",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <RadarRounded sx={{ fontSize: theme.font.sm, color: theme.color.primary.color }} />
                  <span>{t.intro.badge_quest}</span>
                </Box>
              </Box>

              {/* 2. Monumental Hero Display Title & Tactile Spec Badges */}
              <Box>
                <Custom.Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: theme.font.xl,
                      sm: theme.font.xxl,
                      md: theme.font.xxxl,
                    },
                    fontWeight: theme.font.extraBold,
                    lineHeight: 1.08,
                    letterSpacing: "-0.03em",
                    display: "block",
                    color: theme.color.font.color,
                  }}
                >
                  {heroPrefix && (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontFamily: '"Space Mono", monospace',
                        fontSize: {
                          xs: theme.font.xs,
                          sm: theme.font.sm,
                        },
                        fontWeight: theme.font.bold,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: theme.color.font.accent,
                        mb: theme.spacing.xxs,
                      }}
                    >
                      {heroPrefix}
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{
                      position: "relative",
                      display: "inline-block",
                      color: theme.color.font.color,
                      textShadow: isDark
                        ? `3px 3px 0px #000000`
                        : `2px 2px 0px rgba(25, 27, 34, 0.12)`,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        bottom: { xs: 2, md: 6 },
                        width: "100%",
                        height: { xs: 6, md: 10 },
                        backgroundColor: theme.color.primary.color,
                        opacity: isDark ? 0.22 : 0.28,
                        zIndex: -1,
                      },
                    }}
                  >
                    {heroName}
                  </Box>
                </Custom.Typography>

                {/* Tactile Neo-Brutalist Spec Pills (Design System Square Tokens) */}
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mt: theme.spacing.md,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: theme.spacing.xs,
                  }}
                >
                  {headlineTokens.map((token: string, idx: number) => {
                    const isPrimaryRole = idx === 0;
                    return (
                      <Box
                        key={idx}
                        component="span"
                        sx={{
                          px: theme.spacing.sm,
                          py: theme.spacing.xxs,
                          fontFamily: '"Space Mono", monospace',
                          fontSize: {
                            xs: theme.font.xxs,
                            sm: theme.font.xs,
                          },
                          fontWeight: theme.font.bold,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          backgroundColor: isPrimaryRole
                            ? theme.color.primary.color
                            : theme.color.background.accent,
                          color: isPrimaryRole
                            ? theme.color.primary.text
                            : theme.color.font.color,
                          border: `1px solid ${isPrimaryRole
                            ? theme.color.primary.accent
                            : theme.color.border.color
                            }`,
                          borderRadius: theme.border.square,
                          boxShadow: `2px 2px 0px 0px ${isDark ? "#000000" : "#191b22"
                            }`,
                          transition: "transform 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": {
                            transform: "translate(-1px, -1px)",
                            boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"
                              }`,
                          },
                        }}
                      >
                        {token}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* 3. Concise, High-Impact Pitch & Live Availability Telemetry */}
              <Stack spacing={theme.spacing.sm} sx={{ maxWidth: 600, pt: theme.spacing.xxs }}>
                {/* Lead Statement: Punchy, Scannable Hero Hook */}
                <Custom.Typography
                  variant="subtitle1"
                  component="p"
                  sx={{
                    fontSize: {
                      xs: theme.font.md,
                      md: theme.font.lg,
                    },
                    fontWeight: theme.font.semiBold,
                    lineHeight: 1.45,
                    letterSpacing: "-0.015em",
                    color: theme.color.font.color,
                  }}
                >
                  {t.intro.lead}
                </Custom.Typography>

                {/* Live Availability Telemetry Strip (Concise Recruiter Signal) */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: theme.spacing.xs,
                    pt: theme.spacing.xxs,
                  }}
                >
                  <Custom.Typography
                    variant="body2"
                    component="p"
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      color: theme.color.font.accent,
                      fontSize: theme.font.xs,
                      fontWeight: theme.font.medium,
                      letterSpacing: "0.02em",
                      lineHeight: 1.5,
                    }}
                  >
                    {t.intro.tagline}
                  </Custom.Typography>
                </Box>
              </Stack>

              {/* 4. Primary Action Controls */}
              <Common.Social size="large" />
            </Stack>
          </Grid>

          {/* Right Column: Adaptive Hero Dossier / Operator Card */}
          <Grid item xs={12} md={5} lg={5}>
            <Common.Parallax />
          </Grid>
        </Grid>

        {/* Attribute Pedestals leveraging Item.Metric */}
        <Grid
          container
          spacing={theme.spacing.lg}
          sx={{ pt: theme.spacing.xl }}
        >
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr1_label}
              value={t.intro.attr1_value}
              description={t.intro.attr1_desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr2_label}
              value={t.intro.attr2_value}
              description={t.intro.attr2_desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr3_label}
              value={t.intro.attr3_value}
              description={t.intro.attr3_desc}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};